import { Fragment, useCallback, useMemo } from 'react';
import { Tag } from '@carbon/react';
import { useAtomValue } from 'jotai';
import {
  DataTable,
  ExportButton,
  type DataTableColumnDef,
  type DataTableSelectionState,
  type ExportConfig,
  type ExportFilters,
  type ExportFormat,
  type TableState,
} from 'components/data-table';
import { LaunchLink } from 'components/launch-link';
import { Date } from 'components/date';
import { formatTitleRiskValue } from 'pages/crm-tracts-subtracts/utils';
import { AssetExpandedRow } from './asset-expanded-row';
import { AssetNameCell } from './asset-name-cell';
import { useMasterDataView } from './hooks';
import { mapScopeAtom, serverFiltersAtom } from './atoms';
import {
  calcModeField,
  DECIMAL_8,
  deriveSort,
  EMPTY,
  locateColumn,
  MASTER_DATA_PAGE_SIZES,
  toTableFilters,
} from './utils';
import { tagsTextColumn } from 'lib/asset-aliases';
import {
  subtractCountColumns,
  SUBTRACT_COUNT_COLUMNS_HIDDEN,
} from './subtract-count-column-utils';
import type { AssetRow, MasterDataRequest, MasterDataViewProps } from './types';

/**
 * The master_data export body — the read request minus paging (the export always
 * covers the FULL filtered population) and the slots-only edit field. Same wire
 * contract as the read path, so the BE re-runs the identical query server-side.
 * Assembled HERE (the picker-scope atoms live in this subtree's Provider), then
 * handed to the picker's `onExport` — which owns the POST + Pusher download on the
 * always-mounted tearsheet, so closing the picker can't abort an in-flight export.
 */
export type MasterDataExportRequest = Omit<
  MasterDataRequest,
  'page' | 'pageSize' | 'slotOverrides'
>;

/**
 * DataTable needs an index signature on its row type; `AssetRow` (an interface)
 * doesn't carry one, so intersect it here — the same pattern the Division Orders
 * consumer uses.
 */
type AssetTableRow = AssetRow & Record<string, unknown>;

/** Source CRM Tracts datetime format (12-hour, no seconds/timezone). */
const DATETIME_FORMAT = 'M/DD/YYYY h:mm A';

/**
 * Asset (CRM Tract) columns, ported from `components/crm-tracts-table`. Most
 * columns render from `meta.type` alone (text / currency / decimal); only the
 * Name link, the created/updated timestamps, and the title-risk columns carry a
 * custom `cell`. Every column is sortable server-side. (Insights Entity sorts on
 * the BE `insightsEntity` key, which is an array — so that sort is a server no-op
 * until the BE exposes a scalar sort key for it.)
 */
const BASE_ASSET_COLUMNS: DataTableColumnDef<AssetTableRow>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    size: 200,
    // A tract, a wellbore / LTD asset, or an asset with nothing behind it — see AssetNameCell.
    cell: (info) => <AssetNameCell row={info.row.original} />,
  },
  ...subtractCountColumns<AssetTableRow>(),
  // The tract's aliases as comma-delimited text, read when the table is read; blank with none.
  tagsTextColumn<AssetTableRow>({
    accessorKey: 'crmTractTags',
    header: 'Tags',
    size: 180,
  }),
  {
    id: 'entities',
    header: 'Insights Entity',
    size: 150,
    // Sortable, but a server no-op until the BE exposes a scalar sort key —
    // `insightsEntity` is an array.
    accessorFn: (row) => row.entities?.map((entity) => entity.name).join(', '),
    meta: { type: 'text' },
  },
  {
    id: 'crmAccount',
    header: 'CRM Account',
    size: 150,
    accessorFn: (row) => row.account?.name ?? '',
    meta: { type: 'text' },
  },
  {
    accessorKey: 'surveyArea',
    header: 'Survey Area',
    size: 120,
    meta: { type: 'text' },
  },
  {
    accessorKey: 'quarterCall',
    header: 'Quarter Call',
    size: 120,
    meta: { type: 'text' },
  },
  { accessorKey: 'netAcres', header: 'Net Acres', size: 130, meta: DECIMAL_8 },
  {
    accessorKey: 'netAcreageUnits',
    header: 'Net Acreage Units',
    size: 140,
    meta: { type: 'text' },
  },
  {
    accessorKey: 'grossAcres',
    header: 'Gross Acres',
    size: 130,
    meta: DECIMAL_8,
  },
  // The tract's own interest: the sum of its included subtracts' NRI / WI (a wellbore or LTD
  // row's are the asset's own). Blank where no subtract carries one.
  {
    accessorKey: 'nri',
    header: 'Net Revenue Interest',
    size: 160,
    meta: DECIMAL_8,
  },
  {
    accessorKey: 'workingInterest',
    header: 'Net Working Interest',
    size: 160,
    meta: DECIMAL_8,
  },
  {
    // The `assets` endpoint emits `depthRange` as the object { startDepth,
    // endDepth }; the adapter (`mapAssetRow` → `formatDepthRange`) formats it to
    // the display string this column reads. (Not a BE-sortable scalar key.)
    accessorKey: 'depthRange',
    header: 'Depth Range',
    size: 130,
    meta: { type: 'text' },
  },
  {
    accessorKey: 'instrumentType',
    header: 'Instrument Type',
    size: 130,
    meta: { type: 'text' },
  },
  {
    accessorKey: 'vestingDocument',
    header: 'Vesting Document',
    size: 150,
    // Sorts on `vestingStatus`, the scalar the backend decorates beside the indicator
    // (ASSET_FIELD_RENAMES maps the column onto it) — ascending puts what still needs a person
    // first, then verified, then tracts with no deed.
    //
    // Filtering stays off: there is no tableFilters predicate for this column, and offering one
    // over a value the filter pass never sees would narrow the grid to nothing.
    enableColumnFilter: false,
    enableGlobalFilter: false,
    meta: { type: 'text' },
    cell: (info) => {
      const vesting = info.getValue<AssetTableRow['vestingDocument']>();
      // A dash, not blank: most tracts never came through an acquisition and can never have a
      // deed, so "nothing here" is the ordinary answer rather than missing data.
      if (!vesting) return EMPTY;

      const status = vesting.verified ? 'Verified' : 'Unverified';
      return (
        <LaunchLink
          to={vesting.documentHref}
          label={`${status} — open vesting document in a new tab`}
        >
          <Tag type={vesting.verified ? 'green' : 'magenta'}>{status}</Tag>
        </LaunchLink>
      );
    },
  },
  {
    accessorKey: 'createdAt',
    header: 'Created Date',
    // Wider than a single date needs: the column filter packs two date inputs
    // (Start + End) side-by-side, so it must fit two "MM/DD/YYYY" values.
    size: 220,
    // `dateFilterLocalTime`: local-time, blank-safe inline date filter (INS-11272).
    meta: { type: 'datetime', dateFilterLocalTime: true },
    // Custom cell (not meta 'datetime') to match the source format exactly —
    // meta 'datetime' adds seconds + a timezone suffix and converts to local.
    cell: (info) => (
      <Date
        date={info.getValue<string>()}
        format={DATETIME_FORMAT}
        showTime
        fallback={EMPTY}
      />
    ),
  },
  {
    accessorKey: 'updatedAt',
    header: 'Updated Date',
    // Wider than a single date needs: the column filter packs two date inputs
    // (Start + End) side-by-side, so it must fit two "MM/DD/YYYY" values.
    size: 220,
    // `dateFilterLocalTime`: local-time, blank-safe inline date filter (INS-11272).
    meta: { type: 'datetime', dateFilterLocalTime: true },
    cell: (info) => (
      <Date
        date={info.getValue<string>()}
        format={DATETIME_FORMAT}
        showTime
        fallback={EMPTY}
      />
    ),
  },
  {
    accessorKey: 'targetNpvExcluded',
    header: 'Target - Broker',
    size: 160,
    meta: {
      type: 'currency',
      format: { minDecimalPlaces: 0, maxDecimalPlaces: 0 },
    },
  },
  {
    accessorKey: 'maxNpvExcluded',
    header: 'Max - Broker',
    size: 160,
    meta: {
      type: 'currency',
      format: { minDecimalPlaces: 0, maxDecimalPlaces: 0 },
    },
  },
  {
    accessorKey: 'targetNpvIncluded',
    header: 'Target - SIM',
    size: 160,
    meta: {
      type: 'currency',
      format: { minDecimalPlaces: 0, maxDecimalPlaces: 0 },
    },
  },
  {
    accessorKey: 'maxNpvIncluded',
    header: 'Max - SIM',
    size: 160,
    meta: {
      type: 'currency',
      format: { minDecimalPlaces: 0, maxDecimalPlaces: 0 },
    },
  },
  {
    accessorKey: 'titleQuality',
    header: 'Title Quality',
    size: 130,
    cell: (info) => formatTitleRiskValue(info.getValue<string | null>(), EMPTY),
  },
  {
    accessorKey: 'titleRiskFactor',
    header: 'Title Risk Factor',
    size: 150,
    cell: (info) => formatTitleRiskValue(info.getValue<string | null>(), EMPTY),
  },
];

/**
 * Every column wraps long content (row height grows to fit) — `meta.wrap` is
 * injected uniformly while preserving each column's existing `meta` (type/format).
 */
const ASSET_COLUMNS: DataTableColumnDef<AssetTableRow>[] =
  BASE_ASSET_COLUMNS.map((col) => ({
    ...col,
    meta: { ...col.meta, wrap: true, disableFilterAutocomplete: true },
  }));

/**
 * Picker-only "Type" column: the row's instrument type (OGL/UMI) right
 * after the Name column, so modelability is readable at a glance. The far-right
 * Instrument Type column is deliberately KEPT — this is an additional glance
 * column, not a move. Its own `id` (an accessorKey would collide with the
 * existing `instrumentType` column id).
 */
const TYPE_COLUMN: DataTableColumnDef<AssetTableRow> = {
  id: 'type',
  header: 'Type',
  size: 90,
  accessorFn: (row) => row.instrumentType ?? '',
  meta: { type: 'text', wrap: true, disableFilterAutocomplete: true },
};

/**
 * Asset-picker parametrization. When present, the view becomes the
 * picker's selection surface: multi-select checkboxes (no select-all), the extra
 * Type column, `_canSelect: false` on already-added rows, and NO persisted table
 * customizations (the page's persisted state must not bleed into the picker).
 * The CROSS-PAGE selection lives with the picker (this view only reflects the
 * current page's slice and reports flips) — the record-picker-table wiring.
 */
export interface AssetPickerSelection {
  /** The picker's cross-page selection; this page's checkboxes reflect it. */
  selectedIds: ReadonlySet<string>;
  /** Already-added tracts: checkbox visible but disabled, never pre-checked. */
  usedAssetIds: ReadonlySet<string>;
  /** Reports one row's checkbox flip; the picker flips membership + captures display data. */
  onToggle: (rowId: string, row: AssetRow) => void;
  /**
   * Queues the Download: receives the click-time export body (assembled here from
   * table state + picker-scope atoms) and POSTs it. Owned by the always-mounted
   * tearsheet so the Pusher-delivered file survives the picker closing.
   */
  onExport: (body: MasterDataExportRequest) => void;
}

export interface AssetViewProps extends MasterDataViewProps {
  /** Present ⇒ picker mode; absent ⇒ the unchanged page view. */
  picker?: AssetPickerSelection;
}

/**
 * Asset (CRM Tract) view of the Master Data Panel. Renders the ported CRM Tracts
 * columns on `components/data-table` with server-driven pagination, sort, and
 * search via `useMasterDataView`. Read-only in P1. Expanding a tract row shows its
 * slot subtable (the slot allocations touching the tract) and its CRM-subtract
 * subtable (the tract's distinct subtracts) — `AssetExpandedRow`, fed by the
 * adapted `row.slotAllocations` + `row.subtracts`.
 *
 * @param {AssetViewProps} props - The page identity + the view-selector dropdown slot (+ optional picker mode).
 * @return {JSX.Element} The Asset data table.
 */
export function AssetView({
  packageId,
  calculationId,
  viewDropdown,
  picker,
}: AssetViewProps) {
  const {
    rows,
    totalCount,
    isFetching,
    error,
    pagination,
    onPaginationChange,
    onSortChange,
    onGlobalSearchChange,
    onColumnFilterChange,
  } = useMasterDataView({ view: 'assets', packageId, calculationId });

  // Prepend the shared "show on map" pin column to the ported columns. The pin
  // cell reads/writes `locatedRowAtom` itself, so the column takes no args and
  // the set is static. Picker mode slots the Type column right after Name.
  const isPicker = Boolean(picker);
  const columns = useMemo(() => {
    const base = [locateColumn<AssetTableRow>(), ...ASSET_COLUMNS];
    if (!isPicker) {
      return base;
    }
    const [locate, name, ...rest] = base;
    return [locate, name, TYPE_COLUMN, ...rest];
  }, [isPicker]);

  // Picker mode "Download". The picker-scope filter state is read unconditionally
  // (these are the picker's OWN atoms inside its fresh Jotai Provider, so they can
  // only be read HERE) to honor the hook rules; the export config is only BUILT
  // (and passed) in picker mode below, so the page view stays byte-identical (no
  // export button, no behavior change).
  const filters = useAtomValue(serverFiltersAtom);
  const mapScope = useAtomValue(mapScopeAtom);

  // "What you see is what you download": assemble the applied search + column
  // filters + sort + dataset filters + map scope, ALWAYS constrained to modelable
  // tracts. Mirrors the read path's payload discipline (omit each empty half; no
  // `columns` field so the BE exports its default set — the FE-only Type column
  // never reaches the wire). The POST + Pusher download is owned by the tearsheet
  // (`picker.onExport`), which stays mounted so a picker close can't abort it.
  const handleExport = useCallback(
    (
      _format: ExportFormat,
      { globalFilter, columnFilters, sorting }: ExportFilters
    ) => {
      const search = globalFilter?.trim();
      const tableFilters = toTableFilters(columnFilters, columns, 'assets');
      const sort = deriveSort(sorting, 'assets');
      const body: MasterDataExportRequest = {
        view: 'assets',
        packageId,
        ...calcModeField(calculationId),
        interestModelableOnly: true,
        ...(search ? { search } : {}),
        ...(tableFilters.length ? { tableFilters } : {}),
        ...(sort ? { sort } : {}),
        ...(filters.length ? { filters } : {}),
        ...(mapScope ? { mapScope } : {}),
      };
      picker?.onExport(body);
    },
    [picker, columns, packageId, calculationId, filters, mapScope]
  );

  // Only picker mode opts into the table's export feature (a single server-side
  // CSV preset); the page view passes no export config, so no button renders.
  const exportConfig: ExportConfig | undefined = isPicker
    ? {
        exportPresets: [
          { name: 'Export', format: 'csv', onExport: handleExport },
        ],
      }
    : undefined;

  // Picker mode: mark already-added rows unselectable via the DataTable's
  // `_canSelect: false` row sentinel — checkbox visible, disabled, unchecked.
  const usedAssetIds = picker?.usedAssetIds;
  const data = useMemo(() => {
    const tableRows = rows as AssetTableRow[];
    if (!usedAssetIds) {
      return tableRows;
    }
    return tableRows.map((row) => {
      return usedAssetIds.has(String(row.id))
        ? { ...row, _canSelect: false }
        : row;
    });
  }, [rows, usedAssetIds]);

  // Picker mode: the current page's slice of the picker-owned selection —
  // controlled, so paging away and back re-checks from the source of truth.
  const selectedIds = picker?.selectedIds;
  const selection = useMemo<DataTableSelectionState | undefined>(() => {
    if (!selectedIds) {
      return undefined;
    }
    return {
      selectAll: false,
      toggledNodes: (rows as AssetTableRow[])
        .map((row) => String(row.id))
        .filter((id) => selectedIds.has(id)),
    };
  }, [rows, selectedIds]);

  // Report whatever changed on this page back as individual toggles (the
  // record-picker-table wiring — the picker owns the cross-page Set).
  const onSelectionChange = useCallback(
    (state: TableState) => {
      if (!picker) {
        return;
      }
      const reported = new Set(state.selection.toggledNodes);
      (rows as AssetTableRow[]).forEach((row) => {
        const id = String(row.id);
        if (reported.has(id) !== picker.selectedIds.has(id)) {
          picker.onToggle(id, row);
        }
      });
    },
    [picker, rows]
  );

  // Inline column filters (INS-11272): translate the DataTable's ColumnFiltersState
  // to the `tableFilters` wire shape (this view owns the columns + their meta) and
  // hand it up with the snapshot's reset page.
  const onFilterChange = useCallback(
    (state: TableState) =>
      onColumnFilterChange(
        toTableFilters(state.columnFilters, columns, 'assets'),
        state.page
      ),
    [onColumnFilterChange, columns]
  );

  return (
    <DataTable<AssetTableRow>
      data={data}
      columns={columns}
      getRowId={(row) => String(row.id)}
      // `isFetching` (not `isLoading`) so the progress bar + stale-row overlay
      // also show on pagination/sort/search refetches under `keepPreviousData`.
      isLoading={isFetching}
      error={error ?? null}
      // Controlled pagination: useMasterDataView is the single page owner, so a
      // filter-driven reset to page 1 stays in sync with the pager display.
      // Picker mode also controls the selection slice (owned by the picker).
      controlledState={{ pagination, ...(selection && { selection }) }}
      // Picker mode is ephemeral by design — never share (or write) the page
      // view's persisted customizations.
      persistenceKey={picker ? undefined : 'eval3-new-master-data-asset'}
      initialTableState={{ columnVisibility: SUBTRACT_COUNT_COLUMNS_HIDDEN }}
      config={{
        columnFilters: true,
        columnReordering: true,
        // Center every column header label (data cells keep their own alignment).
        centerHeaders: true,
        // Picker mode: per-row checkboxes only — no select-all (the selection
        // must stay bounded to rows the user explicitly ticks).
        ...(picker && {
          selectionType: 'multi' as const,
          isBulkSelectEnabled: false,
        }),
      }}
      // Picker-only Download: rendered via the far-right toolbar slot (not the
      // built-in `config.export`, which sits FIRST) so it lands LAST in the
      // action row, matching the mock's toolbar order. Server-side
      // full-population export; the ExportButton reads the live table state
      // (search/column-filters/sort) from the DataTable context.
      rightToolbarActions={
        exportConfig
          ? [<ExportButton key="picker-download" config={exportConfig} />]
          : undefined
      }
      onSelectionChange={picker ? onSelectionChange : undefined}
      scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
      pageSizes={MASTER_DATA_PAGE_SIZES}
      onPaginationChange={onPaginationChange}
      onSortChange={onSortChange}
      onGlobalSearchChange={onGlobalSearchChange}
      onFilterChange={onFilterChange}
      // Expand a tract to its slot subtable (the slot allocations touching it)
      // and its CRM-subtract subtable (the tract's distinct subtracts).
      renderRowSubComponent={(row) => <AssetExpandedRow row={row.original} />}
      getRowCanExpand={(row) =>
        (row.original.slotAllocations?.length ?? 0) > 0 ||
        (row.original.assetSubtracts?.length ?? 0) > 0
      }
      searchPlaceholder="Search assets..."
      leftToolbarActions={[
        <Fragment key="view-dropdown">{viewDropdown}</Fragment>,
      ]}
    />
  );
}
